'use strict';

/**
 * @fileoverview Value-pattern redaction — sensitive data written into FREE TEXT, whatever key it sits
 * under (SECURITY-HARDENING-GEMS Q-SG-23 "Yes, scan values too" / Q-SG-24 "Yes, one shared rule").
 *
 * Key-based redaction blanks a `password` field whole; it cannot see a card number in `note`, a bearer
 * token inside an error message or a database URL in `detail`. This is the ONE implementation of the
 * value half, used by the server logger and the browser logger alike.
 *
 * Every pattern is written against a FALSE POSITIVE as much as a true one, because every logged string
 * passes through it and a wrongly redacted identifier is the one an engineer needed:
 *
 *   - card numbers: 13–19 digits (single space / dash separators), first digit 2–6 (a digit a card
 *     network issues), Luhn-valid — epoch milliseconds, order numbers and UUID digit runs survive;
 *   - SSNs: DASHED and valid in form (no 000 / 666 / 9xx area, no 00 group, no 0000 serial);
 *   - phones: masked only in an E.164 or separated NANP shape — a bare 10-digit id is an id;
 *   - bearer credentials: 16+ characters, so "Bearer tokens are rotated" is prose;
 *   - name/value secrets: the identifier must END in a term of the CALLER's field vocabulary and be
 *     followed by `=` or `:` — `tokenCount: 5` is a count, `missingSecrets: X` is a name list;
 *   - database values: only inside PostgreSQL's own error templates (`Key (…)=(…)`, `Failing row
 *     contains (…)`, a data exception's quoted input) — a constraint, table, column or type name is
 *     never touched (`redactDatabaseValues`).
 *
 * NO CATASTROPHIC BACKTRACKING: every repetition that could meet a long run of its own characters is
 * bounded (an identifier prefix by 64, an email by RFC 5321's 64 / 253, a URL user / password by 128 /
 * 256), so the cost of a scan is linear in the text; `tests/redactSensitiveText.redos.test.js` holds it to that on
 * adversarial inputs.
 *
 * @module @puffin-core/redaction/redactSensitiveText
 */

const { redactEmail, redactPhone } = require('./masks');
const { redactDatabaseValues, hasDatabaseValueTrigger } = require('./redactDatabaseValues');

/** The ONE replacement text for a redacted secret. */
const REDACTED_VALUE = '[REDACTED]';

/** `scheme://user:password@` — the password is replaced, the user and host kept. */
const URL_CREDENTIALS = /\b([a-z][a-z0-9+.-]{0,31}:\/\/[^\s:/@]{1,128}:)[^\s@/]{1,256}@/gi;
/** A bearer credential of at least 16 characters. */
const BEARER = /\b(Bearer\s{1,8})[A-Za-z0-9\-._~+/]{16,4096}=*/g;
/** A JSON Web Token: three base64url segments, the first a JSON header (`eyJ…`). */
const JWT = /\beyJ[A-Za-z0-9_-]{5,8192}\.[A-Za-z0-9_-]{5,8192}\.[A-Za-z0-9_-]{0,8192}/g;
/** A run of 13–19 digits with optional single space / dash separators, not inside a longer run. */
const CARD_CANDIDATE = /(?<![\d-])\d(?:[ -]?\d){12,18}(?![\d-])/g;
/** A dashed US SSN, valid in form. */
const SSN = /\b(?!000|666|9\d\d)\d{3}-(?!00)\d{2}-(?!0000)\d{4}\b/g;
/** An email address (RFC 5321 lengths) that is not already masked (no `*` immediately before it). */
const EMAIL =
  /(?<![A-Za-z0-9._%+*-])[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9.-]{1,253}\.[A-Za-z]{2,24}\b/g;
/** A phone number in E.164 or a separated NANP shape. */
const PHONE = /\+\d{10,15}\b|(?<![\d-])\(?\b\d{3}\)?[-. ]\d{3}[-.]\d{4}\b(?![\d-])/g;

/**
 * A field name as a separator- and case-tolerant pattern: `refreshToken` → `refresh[_-]?token`, so
 * `refresh_token`, `refresh-token` and `refreshToken` all match (the regex is case-insensitive). Every
 * non-alphanumeric character is a SEPARATOR, so a token carries letters and digits only and needs no
 * regex escaping.
 *
 * @param {string} field - A field name from the caller's sensitive vocabulary
 * @returns {string} Regex source
 */
function fieldPattern(field) {
  return field
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .join('[_-]?');
}

/** Compiled name/value patterns, one entry per distinct field vocabulary. */
const nameValueCache = new Map();

/** Compiled patterns by vocabulary ARRAY identity (a WeakMap, so a discarded array releases its entry). */
const compiledByArray = new WeakMap();

/** Upper bound on distinct vocabularies kept compiled (callers use a handful; clearing costs a compile). */
const NAME_VALUE_CACHE_MAX = 64;

/**
 * Compile the name/value patterns for a field vocabulary.
 *
 * The identifier may carry a PREFIX (`accessToken`, `client_secret`) but must END in the term. Three
 * forms, each with the value extent that cannot half-leak it:
 *
 *   - `json` — a JSON member keeps its quotes: `"apiKey":"[REDACTED]"`;
 *   - `equals` — `name=value` is ONE token (the URL-parameter shape: `?password=x&next=/`), or a
 *     quoted value, or an Authorization-style value (`Bearer …`, `Basic …`) taken whole;
 *   - `colon` — `name: value` is the header / prose shape, whose value may contain spaces
 *     (`password: my secret phrase`), so it runs to the end of its SEGMENT (newline, `,` or `;`), or is
 *     a quoted value. This is the extent the server logger's header pattern always redacted; the shared
 *     rule keeps it rather than weakening a redaction already in production.
 *
 * @param {string[]} fields - The caller's sensitive field names
 * @returns {{json: RegExp, equals: RegExp, colon: RegExp, firstWords: string[]}|null} Compiled patterns
 *   and the lowercase first words their presence check needs, or null for an empty vocabulary
 */
function nameValuePatterns(fields) {
  // Hot path: a logger passes the SAME array on every record, so identity answers without building a
  // key (the server logger's resolved list is one frozen array per process).
  if (compiledByArray.has(fields)) {
    return compiledByArray.get(fields);
  }
  const cacheKey = fields.join('\u0000');
  if (nameValueCache.has(cacheKey)) {
    const known = nameValueCache.get(cacheKey);
    compiledByArray.set(fields, known);
    return known;
  }
  const names = fields.map(fieldPattern).filter(Boolean);
  let compiled = null;
  if (names.length > 0) {
    // Every name pattern begins with the term's FIRST word, so a text containing none of them (case-
    // folded) cannot match — the cheap presence check `redactSensitiveText` runs before these regexes.
    const firstWords = [
      ...new Set(
        fields.map((field) => fieldPattern(field).split('[_-]?')[0].toLowerCase()).filter(Boolean),
      ),
    ];
    const name = `[A-Za-z0-9_-]{0,64}(?:${names.join('|')})(?![A-Za-z0-9])`;
    const quoted = `"[^"]{0,4096}"|'[^']{0,4096}'`;
    const token = `(?:Bearer|Basic|Digest)\\s{1,8}[^\\s,;&"']{1,4096}|${quoted}|[^\\s,;&"'}]{1,4096}`;
    const segment = `${quoted}|[^\\n\\r,;]{1,4096}`;
    compiled = {
      json: new RegExp(`("${name}"\\s{0,8}:\\s{0,8}")[^"]{0,4096}(")`, 'gi'),
      equals: new RegExp(`(\\b${name}\\s{0,8}=\\s{0,8})(?:${token})`, 'gi'),
      colon: new RegExp(`(\\b${name}\\s{0,8}:\\s{0,8})(?:${segment})`, 'gi'),
      firstWords,
    };
  }
  if (nameValueCache.size >= NAME_VALUE_CACHE_MAX) {
    nameValueCache.clear();
  }
  nameValueCache.set(cacheKey, compiled);
  compiledByArray.set(fields, compiled);
  return compiled;
}

/**
 * Luhn checksum over a digit string.
 *
 * @param {string} digits - Digits only
 * @returns {boolean} Whether the checksum holds
 */
function passesLuhn(digits) {
  let sum = 0;
  for (let i = 0; i < digits.length; i += 1) {
    let digit = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
}

/**
 * Is a candidate digit run a payment card number?
 *
 * @param {string} candidate - The matched text (digits and separators)
 * @returns {boolean} Whether it is network-prefixed and Luhn-valid
 */
function isCardNumber(candidate) {
  const digits = candidate.replace(/\D/g, '');
  return /^[2-6]/.test(digits) && passesLuhn(digits);
}

/**
 * Redact sensitive data found in free text.
 *
 * @param {*} text - Any value; only a non-empty string is scanned, anything else is returned unchanged
 * @param {Object} options
 * @param {string[]} options.fields - The caller's sensitive field vocabulary for name/value secrets.
 *   REQUIRED: each logger owns its vocabulary (the server's whole-token list, the browser's list), and
 *   a default here would be a second, silent one. An empty array scans the value patterns only.
 * @param {string} [options.placeholder='[REDACTED]'] - The replacement for a secret
 * @returns {*} The redacted string, or the input unchanged when it is not a string
 * @throws {TypeError} When `fields` is not an array of strings
 */
function redactSensitiveText(text, options) {
  const { fields, placeholder = REDACTED_VALUE } = options || {};
  // A vocabulary already compiled under its array identity was validated when it was compiled.
  if (
    !compiledByArray.has(fields) &&
    (!Array.isArray(fields) || fields.some((field) => typeof field !== 'string'))
  ) {
    throw new TypeError('redactSensitiveText requires options.fields: an array of field names');
  }
  if (typeof text !== 'string' || text.length === 0) {
    return text;
  }

  // PRESENCE pre-checks (the server logs every request): a family whose trigger character cannot be in
  // the text is never run, so a plain message costs a few `includes` calls, not nine global regex
  // passes. Each trigger is one the family's pattern REQUIRES, so a skip can never lose a match
  // (`redactSensitiveText.prefilter.test.js`).
  const hasDigit = /\d/.test(text);
  const hasAt = text.includes('@');

  // Row values PostgreSQL wrote into its own error text go first and whole: a key's value or a
  // failing row is not shaped like a secret, so no family below would see a name or a hash in it.
  let result = hasDatabaseValueTrigger(text) ? redactDatabaseValues(text, placeholder) : text;
  if (text.includes('=') || text.includes(':')) {
    const nameValue = nameValuePatterns(fields);
    const folded = nameValue ? text.toLowerCase() : '';
    if (nameValue && nameValue.firstWords.some((word) => folded.includes(word))) {
      result = result
        .replace(nameValue.json, (_match, open, close) => `${open}${placeholder}${close}`)
        .replace(nameValue.equals, (_match, prefix) => `${prefix}${placeholder}`)
        .replace(nameValue.colon, (_match, prefix) => `${prefix}${placeholder}`);
    }
  }
  if (hasAt && text.includes('://')) {
    result = result.replace(URL_CREDENTIALS, (_match, prefix) => `${prefix}${placeholder}@`);
  }
  if (text.includes('Bearer')) {
    result = result.replace(BEARER, (_match, scheme) => `${scheme}${placeholder}`);
  }
  if (text.includes('eyJ')) {
    result = result.replace(JWT, () => placeholder);
  }
  if (hasDigit) {
    result = result
      .replace(CARD_CANDIDATE, (candidate) => (isCardNumber(candidate) ? placeholder : candidate))
      .replace(SSN, () => placeholder);
  }
  if (hasAt) {
    result = result.replace(EMAIL, (email) => redactEmail(email));
  }
  if (hasDigit) {
    result = result.replace(PHONE, (phone) => redactPhone(phone));
  }
  return result;
}

module.exports = { redactSensitiveText, REDACTED_VALUE };
